export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ScreenBreakPoints = { [key: string | Breakpoint]: number };

const sortBreakpointsValues = (values: ScreenBreakPoints) => {
  const breakpointsAsArray =
    Object.keys(values).map(key => ({ key, val: values[key] })) || [];
  // Sort in ascending order
  breakpointsAsArray.sort(
    (breakpoint1, breakpoint2) => breakpoint1.val - breakpoint2.val
  );
  return breakpointsAsArray.reduce((acc, obj) => {
    return { ...acc, [obj.key]: obj.val };
  }, {});
};

export default function createBreakpoints() {
  const values: ScreenBreakPoints = {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  };

  const unit = 'px';

  const step = 5;

  const sortedValues = sortBreakpointsValues(values);
  const keys = Object.keys(sortedValues) as Breakpoint[];

  function up(key: Breakpoint | number): string {
    const value = typeof values[key] === 'number' ? values[key] : key;
    return `@media (min-width:${value}${unit})`;
  }

  function down(key: Breakpoint | number): string {
    const value = typeof values[key] === 'number' ? values[key] : Number(key);
    return `@media (max-width:${value - step / 100}${unit})`;
  }

  function between(
    start: Breakpoint | number,
    end: Breakpoint | number
  ): string {
    const endIndex = typeof end === 'string' ? keys.indexOf(end) : Number(end);

    return (
      `@media (min-width:${
        typeof values[start] === 'number' ? values[start] : start
      }${unit}) and ` +
      `(max-width:${
        (endIndex !== -1 && typeof values[keys[endIndex]] === 'number'
          ? values[keys[endIndex]]
          : Number(end)) -
        step / 100
      }${unit})`
    );
  }

  function only(key: Breakpoint) {
    if (keys.indexOf(key) + 1 < keys.length) {
      return between(key, keys[keys.indexOf(key) + 1]);
    }

    return up(key);
  }

  function not(key: Breakpoint) {
    // handle first and last key separately, for better readability
    const keyIndex = keys.indexOf(key);

    if (keyIndex === 0) {
      return up(keys[1]);
    }
    if (keyIndex === keys.length - 1) {
      return down(keys[keyIndex]);
    }

    return between(key, keys[keys.indexOf(key) + 1]).replace(
      '@media',
      '@media not all and'
    );
  }

  return {
    keys,
    values: sortedValues,
    up,
    down,
    between,
    only,
    not,
    unit,
  };
}
